import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut,
  Plus, Trash2, Search, Loader2, Tag, BookOpen, Brain, Shield, Share2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = [
  { value: "all", label: "All Categories" },
  { value: "formulation", label: "Formulations" },
  { value: "material", label: "Materials" },
  { value: "processing", label: "Processing" },
  { value: "testing", label: "Testing" },
  { value: "application", label: "Applications" },
  { value: "trained", label: "Uploaded/Trained" },
  { value: "other", label: "Other" },
];

export default function KnowledgePage() {
  const { user, logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "formulation", tags: "" });

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try { const r = await axios.get(`${API}/knowledge`); setEntries(r.data); } catch (e) {}
    finally { setLoading(false); }
  };

  const addEntry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/knowledge`, {
        title: form.title, content: form.content, category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setForm({ title: "", content: "", category: "formulation", tags: "" });
      setDialogOpen(false);
      fetchEntries();
    } catch (e) {} finally { setSubmitting(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await axios.delete(`${API}/knowledge/${id}`); setEntries((e) => e.filter((x) => x.id !== id)); } catch (e) {}
  };

  const toggleShare = async (entry) => {
    const action = entry.is_shared ? "unshare" : "share";
    try {
      await axios.post(`${API}/knowledge/${entry.id}/${action}`);
      setEntries((list) => list.map((x) => x.id === entry.id ? { ...x, is_shared: !entry.is_shared } : x));
    } catch (e) {
      alert("Failed to update share status");
    }
  };

  const filtered = entries.filter((e) =>
    (filterCat === "all" || e.category === filterCat) &&
    (!search || e.title.toLowerCase().includes(search.toLowerCase()) || e.content.toLowerCase().includes(search.toLowerCase()))
  );

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${item.path === "/knowledge" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center"><span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span></div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p></div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg"><LogOut className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <BackButton />
            <Database className="w-5 h-5 text-orange-500" />
            <h1 className="font-heading font-semibold text-slate-900">Knowledge Base</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-knowledge-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> Add Entry</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>Add Knowledge Entry</DialogTitle></DialogHeader>
              <form onSubmit={addEntry} className="space-y-4 mt-4">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="knowledge-title-input" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="knowledge-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.slice(1).map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Content</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="min-h-[150px]" required data-testid="knowledge-content-input" /></div>
                <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="EPDM, sulfur" data-testid="knowledge-tags-input" /></div>
                <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="save-knowledge-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Entry"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search knowledge base..." className="pl-10 h-11 bg-white" data-testid="knowledge-search" />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-48 h-11 bg-white" data-testid="category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12"><BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No entries</p></div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filtered.map((e) => (
                  <div key={e.id} className={`bg-white rounded-xl border p-5 hover:border-orange-300 transition-colors ${e.is_shared ? "border-green-300 ring-1 ring-green-100" : "border-slate-200"}`} data-testid={`knowledge-entry-${e.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading font-semibold text-slate-900">{e.title}</h3>
                      <div className="flex items-center gap-1">
                        {user?.is_admin && (
                          <button onClick={() => toggleShare(e)} data-testid={`share-knowledge-${e.id}`}
                            className={`p-1.5 rounded-lg ${e.is_shared ? "bg-green-100 text-green-700" : "hover:bg-slate-100 text-slate-400 hover:text-green-600"}`}
                            title={e.is_shared ? "Shared with all users — click to unshare" : "Share with all users"}>
                            <Share2 className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => del(e.id)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid={`delete-knowledge-${e.id}`}>
                          <Trash2 className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-3">{e.content}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full capitalize">{e.category}</span>
                        {e.is_shared && <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Shared</span>}
                      </div>
                      {e.tags?.length > 0 && (
                        <div className="flex items-center gap-1"><Tag className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500">{e.tags.slice(0, 2).join(", ")}{e.tags.length > 2 && ` +${e.tags.length - 2}`}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
